// Iter183a — Portfolio Risk Health: pinned nav LED + shared full panel.
// Single source of truth: GET /api/portfolio/risk-health (45s server cache).
// LED polls every 60s; "ml-risk-health-refresh" window event forces a
// bypass-cache refetch (dispatched after trade-log / basket mutations).
import React from "react";
import api from "@/lib/api";

const STATUS_COLOR = {
  green: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
  unknown: "#71717A",
};
const RULE_DOT = {
  ok: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
  not_tracked: "#71717A",
};

export function useRiskHealth() {
  const [health, setHealth] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async (force = false) => {
    try {
      const { data } = await api.get(`/portfolio/risk-health${force ? "?force=true" : ""}`);
      setHealth(data);
    } catch (e) { /* keep last known */ }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => {
    load();
    const t = setInterval(() => load(false), 60000);
    const onMutate = () => load(true);
    window.addEventListener("ml-risk-health-refresh", onMutate);
    return () => { clearInterval(t); window.removeEventListener("ml-risk-health-refresh", onMutate); };
  }, [load]);

  return { health, loading, refresh: () => load(true) };
}

function RuleRow({ r }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b last:border-b-0"
      style={{ borderColor: "var(--hoip-divider)" }}
      data-testid={`risk-rule-${r.id}`}>
      <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0"
        style={{ background: RULE_DOT[r.status] || RULE_DOT.not_tracked }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-medium" style={{ color: r.status === "not_tracked" ? "var(--hoip-text-tertiary)" : "var(--hoip-text-secondary)", fontStyle: r.status === "not_tracked" ? "italic" : "normal" }}>
            {r.label}
            {r.status === "not_tracked" && (
              <span className="ml-1 text-[8px] px-1 rounded-sm align-middle not-italic"
                style={{ background: "rgba(113,113,122,0.15)", color: "#71717A" }}
                data-testid={`risk-nottracked-${r.id}`}>v2</span>
            )}
            {r.approx && (
              <span className="ml-1 text-[8px] px-1 rounded-sm align-middle"
                style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA" }}
                title={r.note || "Approximate value"}
                data-testid={`risk-approx-${r.id}`}>approx</span>
            )}
          </span>
          <span className="text-[10px] font-mono-data whitespace-nowrap"
            style={{ color: r.status === "red" ? "#F87171" : r.status === "amber" ? "#F59E0B" : r.status === "not_tracked" ? "var(--hoip-text-tertiary)" : "var(--hoip-text-primary)", fontStyle: r.status === "not_tracked" ? "italic" : "normal" }}>
            {r.value_display} <span style={{ color: "var(--hoip-text-tertiary)" }}>/ {r.limit_display}</span>
          </span>
        </div>
        {r.reason && (
          <div className="text-[9px] mt-0.5" style={{ color: r.status === "red" ? "#F87171" : "#F59E0B" }}
            data-testid={`risk-reason-${r.id}`}>
            {r.reason}
          </div>
        )}
        {r.status === "not_tracked" && r.note && (
          <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>{r.note}</div>
        )}
      </div>
    </div>
  );
}

export function RiskHealthPanel({ health, compact = false }) {
  if (!health) return null;
  return (
    <div data-testid="risk-health-panel">
      {health.seeded && (
        <div className="text-[9px] mb-1.5 px-1.5 py-0.5 rounded-sm inline-block"
          style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA" }}>
          No positions on file — values are seeded/illustrative.
        </div>
      )}
      <div>
        {(health.rules || []).map(r => <RuleRow key={r.id} r={r} />)}
      </div>
      {!compact && (
        <div className="text-[9px] mt-2 leading-relaxed" style={{ color: "var(--hoip-text-tertiary)" }}>
          Rules-based status against the Portfolio Risk Limits you configured in{" "}
          <a href="/settings" className="underline" style={{ color: "var(--hoip-text-secondary)" }}>Settings</a>.
          Informational only — not a directive to trade.
        </div>
      )}
    </div>
  );
}

export function RiskHealthLED({ collapsed = false }) {
  const { health, loading } = useRiskHealth();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const status = health?.status || "unknown";
  const color = STATUS_COLOR[status] || STATUS_COLOR.unknown;
  const breaches = (health?.rules || []).filter(r => r.status === "amber" || r.status === "red");
  const title = loading ? "Portfolio Risk Health — loading…"
    : breaches.length === 0 ? "Portfolio Risk Health — all limits OK"
    : `Portfolio Risk Health — ${breaches.map(b => b.label).join(", ")} ${breaches.length === 1 ? "limit" : "limits"} flagged`;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-2 py-1.5 rounded-sm text-left transition-colors hover:bg-[var(--hoip-hover)]"
        title={title}
        data-testid="risk-health-led">
        <span className="relative flex-shrink-0">
          <span className="block w-2.5 h-2.5 rounded-full" style={{ background: color }} data-testid="risk-health-dot" />
          {status !== "green" && status !== "unknown" && (
            <span className="absolute inset-0 rounded-full animate-ping opacity-50" style={{ background: color }} />
          )}
        </span>
        {!collapsed && (
          <span className="text-[10px] font-medium" style={{ color: "var(--hoip-text-secondary)" }}>
            Portfolio Risk
            {breaches.length > 0 && (
              <span className="ml-1.5 text-[9px] font-bold px-1 rounded-sm"
                style={{ background: `${color}22`, color }}
                data-testid="risk-health-breach-count">
                {breaches.length}
              </span>
            )}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-full top-0 ml-2 w-80 z-50 rounded-sm border shadow-xl p-3"
          style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
          data-testid="risk-health-popover">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>
              Portfolio Risk Health
            </span>
            <span className="text-[9px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
              {health?.as_of ? `as of ${new Date(health.as_of).toLocaleTimeString()}` : "—"}
            </span>
          </div>
          {loading && !health ? (
            <div className="text-[10px] py-3 text-center" style={{ color: "var(--hoip-text-tertiary)" }}>Loading…</div>
          ) : (
            <RiskHealthPanel health={health} />
          )}
        </div>
      )}
    </div>
  );
}
